import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import Icon1 from "../icons/Icon.js";
import DropCell from "./DropCell.js";
import Spinner from "../loading/Spinner.js";
import SmallSpinner from "../loading/SmallSpinner.js";

const Dropzone1 = ({
  onDrop,
  files = [],
  maxFiles = Infinity,
  maxSize = 52428800,
  dropzoneStyles = "",
  instructionsText = "Glissez vos fichiers ici, ou cliquez pour sélectionner des fichiers",
  onDelete,
  loading,
  mandatory = false,
}) => {
  const [loadingData, setLoadingData] = React.useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      setLoadingData(true);

      const newFiles = [];
      for (const file of acceptedFiles) {
        if (false) {
          // Process .zip files to extract the first XML file
          const extractedFile = await processZipFile(file);
          if (extractedFile) {
            newFiles.push(extractedFile);
          } else {
            console.warn(`No XML file found in the ZIP: ${file.name}`);
          }
        } else {
          newFiles.push(file); // Add non-ZIP files directly
        }
      }

      if (newFiles.length > 0) {
        onDrop([...files, ...newFiles]); // Append new files to existing ones
      }

      setLoadingData(false);
    },
    [onDrop, files]
  );

  const onDropRejected = useCallback(
    (rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        if (file.file.size > maxSize) {
          console.error(
            `Rejected file due to size: ${file.file.name}. Size: ${file.file.size} bytes. Max allowed size: ${maxSize} bytes.`
          );
        } else {
          console.warn(
            `Rejected file: ${file.file.name}, Type: ${file.file.type}`
          );
        }
      });
      const fileNames = rejectedFiles.map((file) => file.file.name);
      alert(`Invalid file types or sizes: ${fileNames.join(", ")}`);
    },
    [maxSize]
  );

  const [onHover, setOnHover] = React.useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    onDropRejected,
    accept: {
      "application/pdf": [],
      "application/xml": [],
      "application/zip": [],
      "application/x-zip-compressed": [],
      "text/xml": [],
      "image/png": [],
      "image/jpeg": [],
    },
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div className="flex flex-col items-center w-full">
      <div
        {...getRootProps()}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
        className={`border-2 border-dashed rounded-lg h-36 w-full flex items-center justify-center text-center p-4 cursor-pointer ${
          mandatory
            ? "border-red-500 bg-red-100"
            : isDragActive
              ? "border-blue-500 bg-blue-50"
              : onHover
                ? "bg-blue-50 border-shadow_25"
                : "border-shadow_25 bg-transparent"
        } ${dropzoneStyles}`}
      >
        <input {...getInputProps()} />
        {loadingData ? (
          <SmallSpinner loading={true} />
        ) : (
          <div className="flex flex-col items-center">
            {isDragActive ? (
              <div className="text-lg font-bold text-gray-700 flex flex-row">
                Lâchez pour déposer
                <Icon1
                  icon_name="join"
                  height="30px"
                  width="30px"
                  fill="primary"
                />
              </div>
            ) : (
              <div className="flex items-center text-h3 gap-2 text-shadow_50">
                {loading ? "Téléchargement en cours ..." : instructionsText}
                {loading && (
                  <SmallSpinner loading={true} margin={0} size={20} />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Display the dropped files */}
      <div className="w-full rounded-lg border shadow flex flex-col gap-1 p-2">
        {files.length !== 0 ? (
          files.map((file, index) => (
            <div
              key={index}
              className={`flex flex-row w-fit items-center gap-2 text-h3 px-2 py-1  rounded-lg
                ${file.name.includes(".pdf") ? "bg-blue-50 text-blue-400 " : "bg-purple-50 text-violet-400 "}
                
                
                `}
            >
              <Icon1
                icon_name={file.name.includes(".pdf") ? "file" : "xml"}
                size={20}
                fill={file.name.includes(".pdf") ? "#2f7bed" : "#7d31cf"}
              />
              {file.name.length > 50
                ? file.name.substring(0, 50) + "..."
                : file.name}
              <div className="flex flex-row gap-2  cursor-pointer align-middle items-center">
                <div className="w-4" onClick={() => onDelete(file)}>
                  <Icon1
                    icon_name="delete"
                    height="16px"
                    width="16px"
                    fill={"#ec1b44"}
                  />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-h3 text-shadow_50 h-full w-full min-h-10 justify-center flex flex-col">
            Pas de documents déposés
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropzone1;

export const processZipFile = async (zipFile) => {
  const zip = new JSZip();
  const content = await zip.loadAsync(zipFile);
  const xmlFile = Object.keys(content.files).find((fileName) =>
    fileName.endsWith(".xml")
  );

  if (xmlFile) {
    const xmlContent = await zip.file(xmlFile).async("string");
    const blob = new Blob([xmlContent], { type: "application/xml" });
    const extractedFile = new File([blob], xmlFile, {
      type: "application/xml",
    });
    return extractedFile;
  }

  return null; // Return null if no XML file is found
};
